import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Call, CallCreateResponse, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { Subscription } from "apollo-client/util/Observable";
import useAuthUser from "hooks/useAuthUser";
import providers from "lib/providers";
import { startRingtone, stopRingtone } from "lib/ringtone";
import BaseAvatar from "components/BaseAvatar";
import emitter, { EVENTS } from "lib/emitter";
import { onNetworkRecovery } from "lib/networkRecovery";
import { isDesktop, incomingCall as desktopCall } from "../../desktop";
import { DESKTOP_CALL_EVENTS } from "hooks/useDesktopIncomingCall";
import { MediaFilesController } from "lib/MediaFileController";
import { useTranslation } from "locales";
import "./style.scss";

const RING_TIMEOUT_MS = 30000;

export default function IncomingCallModal() {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const history = useHistory();
  const [incomingCall, setIncomingCall] = useState<CallCreateResponse | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const incomingSubRef = useRef<Subscription | null>(null);
  const statusSubRef = useRef<Subscription | null>(null);
  /** True if the current ringing call was delegated to the Electron main
   * process (always-on-top call window + dock.bounce). When the main window
   * regains focus we want to take over the ring UX back — but ONLY if we
   * previously delegated, otherwise we'd double-play the ringtone every time
   * the user tabs away and back during a normal in-focus incoming call. */
  const delegatedToNativeRef = useRef(false);

  const dismiss = useCallback(() => {
    setIncomingCall(null);
    stopRingtone();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Tear down any native (Electron) overlay that may be active.
    if (isDesktop) desktopCall.dismiss();
    delegatedToNativeRef.current = false;
  }, []);

  const handleAccept = useCallback(async (voiceOnly = false) => {
    if (!incomingCall) return;
    const callId = incomingCall.call.id;
    dismiss();
    try {
      // eslint-disable-next-line no-console
      console.info(`[call-accept] handleAccept callId=${callId} voiceOnly=${voiceOnly}`);
      await providers.CallsProvider.accept({ callId });
      // eslint-disable-next-line no-console
      console.info(`[call-accept] backend ACCEPTED callId=${callId}`);
      // Cross-tab signal — fired only AFTER the server confirmed the
      // accept. The GraphQL `callStatusUpdated` subscription is the
      // authoritative cross-device dismiss path, but it can lag a couple
      // of seconds when the WebSocket has just reconnected. Other tabs
      // in the same browser see the `storage` event almost instantly
      // (<100 ms) and dismiss their modals without a network round-trip.
      // Cleaned up after 30 s so the key doesn't accumulate.
      //
      // Setting the flag BEFORE awaiting accept would dismiss sibling
      // tabs even when this tab's accept ultimately failed (network /
      // 403 / timeout), leaving the user with no UI to retry on.
      try {
        localStorage.setItem(`call:${callId}:accepted`, "1");
        setTimeout(() => {
          try { localStorage.removeItem(`call:${callId}:accepted`); } catch { /* ignore */ }
        }, 30000);
      } catch { /* ignore — quota / disabled storage */ }
      const subjectType = incomingCall.call.subject?.subjectType;
      const voiceSuffix = voiceOnly ? "?voiceOnly=1" : "";
      if (subjectType === "appointment") {
        history.push(`/appointment/${incomingCall.call.subject.subjectId}${voiceSuffix}`);
      } else if (subjectType === "call") {
        // Direct 1-on-1 call — navigate to dedicated call page
        history.push(`/call/${callId}${voiceSuffix}`, {
          callConnection: incomingCall.connection,
          isCaller: false,
          voiceOnly,
          opponentUser: incomingCall.call.caller,
        });
      } else {
        history.push(`/conference/${incomingCall.call.subject?.subjectId || callId}${voiceSuffix}`);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[call-accept] FAILED callId=${callId}:`, e);
    }
  }, [incomingCall, dismiss, history]);

  const handleDecline = useCallback(async () => {
    if (!incomingCall) return;
    const callId = incomingCall.call.id;
    dismiss();
    try {
      await providers.CallsProvider.end({ callId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[incoming-call] Failed to decline:", e);
    }
  }, [incomingCall, dismiss]);

  // Subscribe to incoming calls
  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        incomingSubRef.current = await providers.CallsProvider.subscribeIncomingCall(
          (data: CallCreateResponse) => {
            setIncomingCall(data);

            // On macOS Desktop, if our window is not in focus (minimized, in
            // tray, or user is in another Space/app), delegate the ring UX
            // to the Electron main process: it will show an always-on-top
            // CallKit-like window, bounce the dock, and play the ringtone.
            // The React modal stays primed in the background and will take
            // over as soon as the user answers (focus returns here).
            const delegateToNative = isDesktop && !document.hasFocus();
            if (delegateToNative) {
              const caller = data.call.caller;
              const callerName = caller?.screenName || caller?.username || "Unknown";
              desktopCall.show({
                callId: String(data.call.id),
                callerName,
                callerAvatarUrl: caller?.avatar
                  ? MediaFilesController.getImageThumbnail(caller.avatar.urlPart, "240p")
                  : undefined,
                // Default to video; the UI can distinguish audio-only via the accept button.
                isVideo: true,
              });
              delegatedToNativeRef.current = true;
            } else {
              startRingtone();
              delegatedToNativeRef.current = false;
            }

            // Auto-dismiss after timeout — dismiss() is the single teardown
            // path (same as the reconcile path below), so future cleanup
            // steps can't diverge between the two timers.
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(dismiss, RING_TIMEOUT_MS);
          }
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[incoming-call] Subscription failed:", e);
      }

      try {
        statusSubRef.current = await providers.CallsProvider.subscribeCallStatusUpdated(
          (call: Call) => {
            setIncomingCall((prev) => {
              if (!prev || String(prev.call.id) !== String(call.id)) return prev;
              // Terminal: dismiss the modal on this device when ANY status
              // moves the call out of NEW. ACCEPTED is included so that
              // when the user answers on one device (tab / Electron /
              // CallKit), every other device showing the same incoming
              // call collapses the ring UX instantly — without this branch
              // the modal sat open with the ringtone playing on all other
              // surfaces until the user dismissed them manually. The
              // device that actually accepted has already called dismiss()
              // synchronously inside handleAccept; by the time this event
              // arrives `prev` is null on that device and we short-circuit
              // above.
              if (
                call.status === CallStatus.CANCELLED ||
                call.status === CallStatus.ENDED ||
                call.status === CallStatus.DECLINED ||
                call.status === CallStatus.ACCEPTED
              ) {
                stopRingtone();
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                  timeoutRef.current = null;
                }
                // Tear down the native overlay (if present) otherwise it would
                // keep ringing on desktop until the 30s auto-timeout fires.
                if (isDesktop) desktopCall.dismiss();
                delegatedToNativeRef.current = false;
                return null;
              }
              return prev;
            });
          }
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[incoming-call] Status subscription failed:", e);
      }
    })();

    return () => {
      if (incomingSubRef.current) incomingSubRef.current.unsubscribe();
      if (statusSubRef.current) statusSubRef.current.unsubscribe();
      stopRingtone();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // If user logs out while an incoming call is still ringing, tear down
      // the native overlay so it doesn't linger with an orphaned ringtone.
      if (isDesktop) desktopCall.dismiss();
      delegatedToNativeRef.current = false;
    };
  }, [user]);

  // Cross-tab dismiss via localStorage. `handleAccept` on the accepting
  // tab writes `call:<id>:accepted`; sibling tabs in the same browser
  // receive the storage event almost instantly and dismiss without
  // waiting for the GraphQL subscription round-trip. Pure backup —
  // device-spanning dismiss still goes through the subscription.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key || !e.newValue) return;
      const match = /^call:(.+):accepted$/.exec(e.key);
      if (!match) return;
      const acceptedId = match[1];
      setIncomingCall((prev) => {
        if (!prev || String(prev.call.id) !== String(acceptedId)) return prev;
        stopRingtone();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (isDesktop) desktopCall.dismiss();
        delegatedToNativeRef.current = false;
        return null;
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /**
   * Relay native Answer / Decline (from Electron overlay, system Notification
   * or tray menu) into the same handlers the React buttons use. Events are
   * emitted by `useDesktopIncomingCall` hook, which listens on the IPC bridge.
   */
  useEffect(() => {
    if (!isDesktop) return;
    const onNativeAnswer = () => { handleAccept(false); };
    const onNativeDecline = () => { handleDecline(); };
    emitter.on(DESKTOP_CALL_EVENTS.NATIVE_ANSWER, onNativeAnswer);
    emitter.on(DESKTOP_CALL_EVENTS.NATIVE_DECLINE, onNativeDecline);
    return () => {
      emitter.off(DESKTOP_CALL_EVENTS.NATIVE_ANSWER, onNativeAnswer);
      emitter.off(DESKTOP_CALL_EVENTS.NATIVE_DECLINE, onNativeDecline);
    };
  }, [handleAccept, handleDecline]);

  /**
   * Relay native CallKit Accept (from iOS PushKit → useNativeCallKit) into
   * the SAME `handleAccept` path used by the in-page accept button. The
   * native layer no longer submits the accept mutation or navigates —
   * that must happen here because only this component holds the
   * `incomingCall.connection` (WebRTC signalling handle) from the
   * GraphQL subscription, and `/call/:id` needs it in route state to
   * join the caller's media session. Without this, 1-on-1 direct calls
   * stick on "Connecting..." for both participants.
   *
   * Races we defend against:
   * 1. CallKit Accept fires BEFORE `subscribeIncomingCall` has delivered
   *    its payload (cold-launch, or a backgrounded app with a still-
   *    reconnecting GraphQL WebSocket). We wait briefly for the
   *    subscription to land, then fall back to `findActive()` which
   *    returns the same `CallCreateResponse` shape (call + connection).
   * 2. Stale closures. Listener is registered once with `[]` deps and
   *    reads the latest `incomingCall` and `handleAccept` via refs.
   *    The previous wiring re-registered on every state change and
   *    captured a stale `handleAccept` (bound to incomingCall=null)
   *    inside its setInterval, so a subscription event that arrived
   *    AFTER Accept silently failed: the polling tick called the
   *    stale handler which short-circuited on `if (!incomingCall) return;`.
   */
  const incomingCallRef = useRef<CallCreateResponse | null>(null);
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);
  const handleAcceptRef = useRef(handleAccept);
  useEffect(() => { handleAcceptRef.current = handleAccept; }, [handleAccept]);

  useEffect(() => {
    const matches = (acceptedId: string) => {
      const c = incomingCallRef.current;
      return !!c && String(c.call.id) === String(acceptedId);
    };

    const onExternalAnswer = async (payload: unknown) => {
      const acceptedId = (payload as { callId?: string } | undefined)?.callId;
      if (!acceptedId) return;

      // Fast path — subscription already populated the payload.
      if (matches(acceptedId)) {
        handleAcceptRef.current(false);
        return;
      }

      // Wait briefly for the subscription to deliver. Normal latency is
      // well under a second; the 2s cap exists so a permanently-missed
      // event (WebSocket reconnected after the server fired its
      // `incomingCall` event and didn't replay) falls through to
      // findActive() instead of hanging.
      const deadline = Date.now() + 2000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 100));
        if (matches(acceptedId)) {
          handleAcceptRef.current(false);
          return;
        }
      }

      // findActive() returns the same `CallCreateResponse` shape, so
      // synthesising an incomingCall lets handleAccept navigate with
      // the WebRTC signalling handle just as if the subscription had
      // landed. Update the ref synchronously before invoking — the
      // ref-sync useEffect won't have flushed yet on this tick.
      try {
        const active = await providers.CallsProvider.findActive();
        const found = active.find((c) => String(c.call.id) === String(acceptedId));
        if (!found) {
          // eslint-disable-next-line no-console
          console.warn("[incoming-call] external answer: call not in findActive", acceptedId);
          return;
        }
        incomingCallRef.current = found;
        setIncomingCall(found);
        handleAcceptRef.current(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[incoming-call] external answer fallback failed", e);
      }
    };
    const onExternalCancel = (payload: unknown) => {
      const cancelledId = (payload as { callId?: string } | undefined)?.callId;
      if (!cancelledId) return;
      if (!matches(cancelledId)) return;
      // Native CallKit / system overlay told us the call ended out-of-band
      // (caller hung up before we picked up, OS auto-timeout, etc.).
      // Bring the React modal + ringtone + desktop overlay in line so we
      // don't keep ringing on top of a dismissed native UI.
      dismiss();
    };
    emitter.on(EVENTS.CALL_ANSWERED_EXTERNALLY, onExternalAnswer);
    emitter.on(EVENTS.CALL_CANCELLED_EXTERNALLY, onExternalCancel);
    return () => {
      emitter.off(EVENTS.CALL_ANSWERED_EXTERNALLY, onExternalAnswer);
      emitter.off(EVENTS.CALL_CANCELLED_EXTERNALLY, onExternalCancel);
    };
  }, [dismiss]);

  // Reconcile local ring state against the server's authoritative
  // findActive(). Runs (a) once on mount — opening/cold-starting the app
  // while a call is ringing must surface it even though the
  // `subscribeIncomingCall` event fired before our socket connected, and
  // (b) on every network-recovery signal (mobile resume, OS reconnect,
  // WebSocket re-establish) — a call that started ringing while offline
  // never replays on the socket.
  //
  // The same reconciliation closes a modal that should already be
  // gone: if the remote hung up while our socket was down, we never
  // received the CANCELLED/ENDED status event; findActive being
  // authoritative ("here is everything still ringing") lets us drop
  // the stale modal.
  const reconcileIncomingCall = useCallback(async (reason: string) => {
    if (!user) return;
    try {
      // Snapshot the modal identity BEFORE the await: if a fresh ring lands
      // via subscribeIncomingCall while findActive is in flight, the stale
      // "server says nothing is ringing" response must not dismiss it.
      const idBefore = incomingCallRef.current ? String(incomingCallRef.current.call.id) : null;
      const active = await providers.CallsProvider.findActive();
      // Match the same condition the server uses for "ringing": NEW
      // status and the current user is the receiver. ACCEPTED calls
      // are already in progress (the call page owns the UX).
      const pending = active.find(
        (c) =>
          c.call.status === CallStatus.NEW &&
          String(c.call.receiverId ?? c.call.receiver?.id) === String(user.id),
      );
      if (!pending) {
        // Server says nothing is ringing — if we STILL show the same modal
        // we had before the request went out, it's stale: dismiss it.
        // Without this the user stares at a phantom incoming-call UI until
        // the 30 s ring timeout fires. A modal that appeared mid-flight is
        // newer than the snapshot and must survive.
        if (incomingCallRef.current && String(incomingCallRef.current.call.id) === idBefore) {
          // eslint-disable-next-line no-console
          console.info(`[incoming-call] dismissing stale modal after ${reason} (server has no active ring)`);
          dismiss();
        }
        return;
      }
      // Avoid replacing a modal that's already showing the same call.
      if (incomingCallRef.current && String(incomingCallRef.current.call.id) === String(pending.call.id)) return;
      // Answer-flow race guard: right after a native Answer (Android deep
      // link / CallKit) the user is already ON the call page while the call
      // is still NEW server-side for a beat. Surfacing the modal + ringtone
      // on top of the accepting call page would fight the page's own accept.
      // The call page runs findActive itself, so it needs no help from us.
      // Segment-boundary match: being on /call/1234 must NOT suppress a
      // different ringing call 123.
      const path = window.location.pathname;
      const callPath = `/call/${pending.call.id}`;
      if (path === callPath || path.startsWith(`${callPath}/`)) return;
      // eslint-disable-next-line no-console
      console.info(`[incoming-call] surfaced via findActive after ${reason}:`, pending.call.id);
      incomingCallRef.current = pending;
      setIncomingCall(pending);
      if (!isDesktop || document.hasFocus()) startRingtone();
      // Mirror the subscription path's auto-dismiss so a surfaced ring
      // can't sit on screen forever if every terminal signal is missed.
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(dismiss, RING_TIMEOUT_MS);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[incoming-call] ${reason} findActive failed:`, e);
    }
  }, [user, dismiss]);

  useEffect(() => {
    if (!user) return;
    reconcileIncomingCall("mount");
    const unsub = onNetworkRecovery(reconcileIncomingCall);
    return unsub;
  }, [user, reconcileIncomingCall]);

  /**
   * If the user brings the main SMeet window back to focus while the native
   * overlay is still displayed, dismiss the overlay so only one call UI
   * (the React modal) and one ringtone are active. We gate on
   * `delegatedToNativeRef` so a normal in-focus incoming call (where the
   * user briefly tabs away and back) doesn't re-trigger `startRingtone`.
   */
  useEffect(() => {
    if (!isDesktop || !incomingCall) return;
    const onFocus = () => {
      if (!delegatedToNativeRef.current) return;
      desktopCall.dismiss();
      delegatedToNativeRef.current = false;
      startRingtone(); // now that we own the foreground, play the ring here
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [incomingCall]);

  if (!incomingCall) return null;

  const caller = incomingCall.call.caller;
  const callerName = caller?.screenName || caller?.username || t("Unknown");

  return (
    <div className="incoming-call-overlay">
      <div className="incoming-call-modal">
        <div className="incoming-call-modal__pulse" />
        {caller && (
          <BaseAvatar className="incoming-call-modal__avatar" size={72} user={caller} />
        )}
        <div className="incoming-call-modal__info">
          <span className="incoming-call-modal__name">{callerName}</span>
          <span className="incoming-call-modal__label">{t("Incoming call...")}</span>
        </div>
        <div className="incoming-call-modal__actions">
          <button
            className="incoming-call-modal__btn incoming-call-modal__btn--decline"
            onClick={handleDecline}
            type="button"
            title={t("Decline")}
          >
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path
                d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 010-1.36C3.69 8.68 7.62 7 12 7s8.31 1.68 11.71 4.72c.18.18.29.44.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 00-2.67-1.85.996.996 0 01-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"
                fill="currentColor"
              />
            </svg>
          </button>
          <button
            className="incoming-call-modal__btn incoming-call-modal__btn--voice"
            onClick={() => handleAccept(true)}
            type="button"
            title={t("Voice only")}
          >
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path
                d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"
                fill="currentColor"
              />
            </svg>
          </button>
          <button
            className="incoming-call-modal__btn incoming-call-modal__btn--accept"
            onClick={() => handleAccept(false)}
            type="button"
            title={t("Video call")}
          >
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path
                d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
